import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// Create a custom axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:4004'}/api/v1`,
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the axios instance for direct access when needed
export const axiosInstance = api;

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // You can handle successful responses here
    return response.data;
  },
  (error) => {
    // Handle errors globally
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response Error:', error.response.data);
      console.error('Status:', error.response.status);
      console.error('Headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request Error:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API methods
export const apiService = {
  // GET request
  get: async (url:string, config = {}) => {
    try {
      return await api.get(url, config);
    } catch (error) {
      throw error;
    }
  },

  // POST request
  post: async <T = any>(url: string, data = {}, config: AxiosRequestConfig = {}): Promise<T> => {
    try {
      const response = await api.post<T>(url, data, config);
      // response is already the parsed body due to response interceptor
      return response as unknown as T;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url:string, data = {}, config = {}) => {
    try {
      return await api.put(url, data, config);
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url:string, config = {}) => {
    try {
      return await api.delete(url, config);
    } catch (error) {
      throw error;
    }
  },

  // PATCH request
  patch: async (url:string, data = {}, config = {}) => {
    try {
      return await api.patch(url, data, config);
    } catch (error) {
      throw error;
    }
  },
};

export default apiService;
